import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { notificationAPI } from '../lib/api';
import { formatDateTimeLong } from '../lib/dateFormat';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Bell, CheckCircle2, ClipboardList, MessageSquare, UserPlus, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TYPE_ICONS = {
  task_assigned: ClipboardList,
  task_status_changed: Clock,
  task_comment: MessageSquare,
  team_invite: UserPlus,
};
const TYPE_COLORS = {
  task_assigned: 'text-blue-400',
  task_status_changed: 'text-amber-400',
  task_comment: 'text-cyan-400',
  team_invite: 'text-emerald-400',
};

export default function NotificationsPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    notificationAPI.list(50)
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const markAllRead = async () => {
    await notificationAPI.markAllRead();
    setData(d => ({ ...d, items: d.items.map(i => ({ ...i, read: true })), unread: 0 }));
  };

  const markRead = async (id) => {
    await notificationAPI.markRead(id);
    setData(d => ({
      ...d,
      items: d.items.map(i => i.id === id ? { ...i, read: true } : i),
      unread: Math.max(0, d.unread - 1)
    }));
  };

  return (
    <div className="max-w-3xl mx-auto p-8 space-y-6" data-testid="notifications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold tracking-tight">Notifications</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {data.unread > 0 ? `${data.unread} unread` : 'All caught up'}
          </p>
        </div>
        {data.unread > 0 && (
          <Button variant="outline" size="sm" onClick={markAllRead} data-testid="mark-all-read">
            <CheckCircle2 className="h-4 w-4 mr-1.5" /> Mark all read
          </Button>
        )}
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-secondary/30 rounded-lg animate-pulse" />)}</div>
      ) : data.items.length === 0 ? (
        <div className="text-center py-24">
          <Bell className="h-12 w-12 text-muted-foreground/20 mx-auto mb-4" />
          <p className="text-lg font-medium text-muted-foreground">No notifications yet</p>
        </div>
      ) : (
        <div className="space-y-1">
          {data.items.map(n => {
            const Icon = TYPE_ICONS[n.type] || Bell;
            const color = TYPE_COLORS[n.type] || 'text-muted-foreground';
            return (
              <button
                key={n.id}
                onClick={() => { markRead(n.id); if (n.link) navigate(n.link); }}
                className={`flex items-start gap-3 w-full py-3 px-4 rounded-xl text-left transition-colors ${n.read ? 'opacity-60 hover:opacity-80' : 'hover:bg-secondary/30'}`}
                data-testid={`notification-${n.id}`}
              >
                <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${color}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${n.read ? '' : 'font-medium'}`}>{n.title}</p>
                  {n.message && <p className="text-xs text-muted-foreground mt-0.5">{n.message}</p>}
                  <p className="text-[10px] text-muted-foreground/50 mt-1">{formatDateTimeLong(n.created_at)}</p>
                </div>
                {!n.read && <span className="h-2 w-2 rounded-full bg-blue-400 shrink-0 mt-2" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
